import type { ICellRendererParams, IHeaderGroupParams } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { MentionsInput, Mention } from 'react-mentions';
import FlipMultiSelect from './FlipMultiSelect';
import CellReactSelect from './CellReactSelect';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import { checkCurrency, checkIsNumber, restrictToTwoDecimals, roundingOff } from '../../../../../Components/helper';
import { NewCheckbox, NewDatePicker } from '../../../../../Components/PIM/common';
import {
  BareInput,
  CellFlex,
  CellInput,
  CellLabel,
  ComboBox,
  ComboDivider,
  ComboLabel,
  CurrencyPrefix,
  DisplayField,
  GroupHeaderBox,
  GroupHeaderLabel,
  GroupHeaderNote,
  HeaderCaretBtn,
  HeaderMenuButton,
  InviteIcon,
  ProductHeaderBox,
  RedirectIcon,
  RowCaret,
  RowCaretSpacer,
  Stack,
  StackItem,
  UomActions,
  YesNoPillButton,
} from './cells.styled';
import type {
  CheckLabelParams,
  CheckSelectParams,
  CoreProductLine,
  CoreStackParams,
  CostingErrorField,
  DisplayCellParams,
  EmailFieldParams,
  GradingGroupLine,
  GradingStackParams,
  GroupHeaderExtraParams,
  MultiSubmitGridContext,
  MultiSubmitRow,
  PlainSelectParams,
  SelectOption,
  TextInputParams,
} from '../types';
import { filterMentionOptions, searchMentionOptions } from '../mentionFilter';
import type { MentionOption } from '../mentionFilter';
import { resolveOptions, sampleTypeOptions } from '../cellFill';
import { gradingSizeOptions } from '../cellFillStack';

type Params = ICellRendererParams<MultiSubmitRow>;

const ctxOf = (params: Params): MultiSubmitGridContext => params.context as MultiSubmitGridContext;

type GroupParams = IHeaderGroupParams<MultiSubmitRow>;

/** Group header for the Product Number group: select-all + expand-all-rows caret + label + column menu. */
export function ProductNumberGroupHeader(params: GroupParams) {
  const ctx = params.context as MultiSubmitGridContext;
  return (
    <ProductHeaderBox onContextMenu={(e) => e.preventDefault()}>
      <NewCheckbox size="sm" checked={ctx.allSelected} onChange={(e) => ctx.toggleSelectAll(e.target.checked)} aria-label="Select all rows" />
      {ctx.anyRowExpandable ? (
        <RowCaret
          $open={ctx.allRowsExpanded}
          onClick={() => ctx.toggleAllRowsExpanded()}
          aria-label={ctx.allRowsExpanded ? 'Collapse all rows' : 'Expand all rows'}
        />
      ) : (
        <RowCaretSpacer aria-hidden="true" />
      )}
      <GroupHeaderLabel>{params.displayName}</GroupHeaderLabel>
      <HeaderMenuButton
        type="button"
        aria-label="Column options"
        title="Column options"
        onClick={(e) => {
          e.stopPropagation();
          ctx.toggleProductColumnMenu();
        }}
      >
        <i className="icon-More" />
      </HeaderMenuButton>
    </ProductHeaderBox>
  );
}

/** Coloured column-group header: [☐] name [caret]  …  [note]. */
export function GroupHeader(params: GroupParams & GroupHeaderExtraParams) {
  const ctx = params.context as MultiSubmitGridContext;
  const groupId = params.columnGroup.getGroupId();
  const expandable = params.columnGroup.isExpandable();
  const expanded = params.columnGroup.isExpanded();
  return (
    <GroupHeaderBox onContextMenu={(e) => e.preventDefault()}>
      {params.showCheckbox && (
        <NewCheckbox
          size="sm"
          checked={ctx.groupChecks[groupId] ?? false}
          onChange={() => ctx.toggleGroupCheck(groupId)}
          aria-label={`Select ${params.displayName}`}
        />
      )}
      <GroupHeaderLabel>{params.displayName}</GroupHeaderLabel>
      {expandable && (
        <HeaderCaretBtn $open={expanded} onClick={() => params.setExpanded(!expanded)} aria-label={expanded ? 'Collapse group' : 'Expand group'} />
      )}
      {expanded && params.note && <GroupHeaderNote>{params.note}</GroupHeaderNote>}
    </GroupHeaderBox>
  );
}

/** Product number cell: row-select checkbox + expand caret + label. Clicking the label marks the row as the copy source. */
export function ProductNumberCell(params: Params) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const isSource = ctx.sourceRowId === row.id;
  const hasMultipleGradingLines = row.gradingLines.length > 1;
  return (
    <CellFlex>
      <NewCheckbox
        size="sm"
        checked={row.selected}
        disabled={isSource}
        onChange={(e) => ctx.updateRow(row.id, { selected: e.target.checked })}
        aria-label={`Select ${row.productNumber}`}
      />
      {hasMultipleGradingLines ? (
        <RowCaret $open={row.expanded} onClick={() => ctx.toggleRowExpanded(row.id)} aria-label="Expand row" />
      ) : (
        <RowCaretSpacer aria-hidden="true" />
      )}
      <CustomTooltip placement="top" tooltipClass="" tooltipText={row.productNumber}>
        <CellLabel as="button" type="button" $active={isSource} onClick={() => ctx.setSourceRow(row.id)}>
          {row.productNumber}
        </CellLabel>
      </CustomTooltip>
      <RedirectIcon aria-label="Open product detail" title="Open product detail" onClick={() => ctx.openProductDetail(row.id)}>
        <span className="icon-redirect f-20" />
      </RedirectIcon>
    </CellFlex>
  );
}

/** Checkbox + multiselect inside one bordered box: [ ☐ │ multiselect ⌄ ]. */
export function CheckSelectCell(params: Params & CheckSelectParams) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const checked = row[params.checkedField];
  const options = ctx.optionsFor(row.id)[params.optionsKey] as SelectOption[];
  return (
    <CellFlex>
      <ComboBox>
        <NewCheckbox
          size="sm"
          checked={checked}
          onChange={(e) => ctx.updateRow(row.id, { [params.checkedField]: e.target.checked } as Partial<MultiSubmitRow>)}
        />
        <ComboDivider />
        <FlipMultiSelect
          options={options}
          value={row[params.valueField]}
          onChange={(selected: SelectOption[]) => ctx.updateRow(row.id, { [params.valueField]: selected } as Partial<MultiSubmitRow>)}
          labelledBy={params.placeholder}
          overrideStrings={{ selectSomeItems: params.placeholder }}
          className="product-custom-multiselect common-new-multiselect mssg-ms-ph w-100"
          disableSearch
        />
      </ComboBox>
    </CellFlex>
  );
}

/** Checkbox + fixed label inside one bordered box: [ ☐ │ Label ] (Technical Package items). */
export function CheckLabelCell(params: Params & CheckLabelParams) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const checked = row[params.checkedField];
  return (
    <CellFlex>
      <ComboBox>
        <NewCheckbox
          size="sm"
          checked={checked}
          onChange={(e) => ctx.updateRow(row.id, { [params.checkedField]: e.target.checked } as Partial<MultiSubmitRow>)}
        />
        <ComboDivider />
        <CustomTooltip placement="top" tooltipClass="" tooltipText={params.label}>
          <ComboLabel>{params.label}</ComboLabel>
        </CustomTooltip>
      </ComboBox>
    </CellFlex>
  );
}

/** Sample(s) cell: checkbox + dropdown + No/Yes "combine" pill. */
export function SampleCell(params: Params) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const options = ctx.optionsFor(row.id).sampleOptions;
  return (
    <CellFlex>
      <ComboBox>
        <NewCheckbox size="sm" checked={row.tpSample} onChange={(e) => ctx.updateRow(row.id, { tpSample: e.target.checked })} />
        <ComboDivider />
        <FlipMultiSelect
          options={options}
          value={row.tpSampleValue}
          onChange={(selected: SelectOption[]) => ctx.updateRow(row.id, { tpSampleValue: selected })}
          labelledBy={ctx.t('TP.SAMPLES')}
          overrideStrings={{ selectSomeItems: ctx.t('TP.SAMPLES') }}
          className="product-custom-multiselect common-new-multiselect mssg-ms-ph w-100"
          disableSearch
        />
      </ComboBox>
      <YesNoPillButton $yes={row.tpCombineYes} onClick={() => ctx.updateRow(row.id, { tpCombineYes: !row.tpCombineYes })} title={ctx.t('TP.COMBINE_TITLE')}>
        {String(row.tpCombineYes ? ctx.t('PILL.YES') : ctx.t('PILL.NO'))}
      </YesNoPillButton>
    </CellFlex>
  );
}

/** A standalone dropdown (Request Sample header fields). Options resolve in priority order:
 *  inline `options` → per-row `optionsKey` → page-level `ctxOptionsKey`. */
export function PlainSelectCell(params: Params & PlainSelectParams) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const resolved = resolveOptions(params, row, ctx);
  const onChange = (value: string) => {
    if (params.isSampleTypeGroup) return ctx.setSampleTypeGroup(row.id, value);
    if (params.isSampleType) return ctx.setSampleType(row.id, value);
    ctx.updateRow(row.id, { [params.valueField]: value } as Partial<MultiSubmitRow>);
  };
  /* Only gated Costing Information fields are ever reported as errors, so a plain
     Request Sample select can never light up here. */
  const hasError = !!params.costingGated && ctx.errorsFor(row.id).includes(params.valueField as CostingErrorField);
  return (
    <CellFlex>
      <CellReactSelect
        value={row[params.valueField]}
        options={resolved}
        placeholder={params.placeholder}
        onChange={onChange}
        disabled={(params.disabled ?? false) || (!!params.costingGated && !row.ciSupplier)}
        error={hasError}
        isClearable={params.clearable ?? false}
      />
    </CellFlex>
  );
}

/** Sample Type cell — mirrors the Samples page (handleSampleTypeGroupChange in Samples.js):
 *  options derive from the selected group's `sampleTypeAutomation` (main bucket) or `otherSamplesList`.
 *  Selecting a group seeds the sample type from the chain, but the user may override it with any
 *  type the group carries — the same freedom the Samples page add-sample modal gives. */
export function SampleTypeCell(params: Params & { placeholder: string }) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const options = sampleTypeOptions(row, ctx);
  return (
    <CellFlex>
      <CellReactSelect
        value={row.sampleType}
        options={options}
        placeholder={params.placeholder}
        onChange={(value) => ctx.setSampleType(row.id, value)}
        disabled={!row.sampleTypeGroup}
      />
    </CellFlex>
  );
}

/** Request Sample Supplier — independent of the page-level Supplier and always user-editable. When the
 *  row attaches to an existing sample chain, the chain's last supplier is only a pre-fill, applied when
 *  that supplier is offered on this page. */
export function RsSupplierCell(params: Params & { placeholder: string }) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  return (
    <CellFlex>
      <CellReactSelect
        value={row.rsSupplier}
        options={ctx.supplierOptions}
        placeholder={params.placeholder}
        onChange={(v) => ctx.updateRow(row.id, { rsSupplier: v })}
      />
    </CellFlex>
  );
}

/** Due date picker bound to the row — master-design NewDatePicker, ISO YYYY-MM-DD in/out. */
export function DueDateCell(params: Params) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const selected = row.dueDate ? new Date(row.dueDate) : null;
  const handleChange = (date: Date | null) => {
    if (!date) {
      ctx.updateRow(row.id, { dueDate: '' });
      return;
    }
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    ctx.updateRow(row.id, { dueDate: `${yyyy}-${mm}-${dd}` });
  };
  return (
    <CellFlex>
      <NewDatePicker
        value={selected && !isNaN(selected.getTime()) ? selected : null}
        onChange={handleChange}
        placeholder="Due Date"
        portalId="mssg-date-portal"
      />
    </CellFlex>
  );
}

/** react-mentions style for in-grid email cells — flush input, body-portalled dropdown. */
const MENTION_STYLE = {
  control: {
    backgroundColor: '#fff',
    fontSize: 14,
    fontWeight: 'normal',
    border: '1px solid #D8D8D8',
    borderRadius: 4,
    height: 28,
    boxSizing: 'border-box',
  },
  '&singleLine': {
    display: 'block',
    width: '100%',
    highlighter: { padding: '0 6px', border: 'none', height: 26, lineHeight: '26px' },
    input: { padding: '0 6px', border: 'none', outline: 'none', height: 26, lineHeight: '26px' },
  },
  suggestions: {
    list: {
      backgroundColor: '#fff',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 14,
      width: '260px',
      maxHeight: '180px',
      overflowY: 'auto',
    },
    item: {
      padding: '5px 12px',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
      color: '#000',
      '&focused': { backgroundColor: '#f5f5f5' },
    },
  },
};

/**
 * Recipient (To/CC/BCC) `@`-mention input. Drives react-mentions from a local draft so typing does not
 * round-trip through the grid's row state on every keystroke — that round-trip made react-mentions reset
 * the caret to the end of the field. The draft is committed to the row on blur; because the row value only
 * changes on blur (not per keystroke), the sync effect never clobbers an in-progress edit. External row
 * changes (e.g. copy-from-source) re-sync the draft on the next render.
 */
function MentionEmailInput({
  value,
  options,
  placeholder,
  onCommit,
}: {
  value: string;
  options: MentionOption[];
  placeholder: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <MentionsInput
      singleLine
      className="ms-mention"
      style={MENTION_STYLE}
      placeholder={placeholder}
      value={draft}
      suggestionsPortalHost={document.body}
      allowSuggestionsAboveCursor
      onBlur={(_event, clickedSuggestion) => {
        if (!clickedSuggestion && draft !== value) onCommit(draft);
      }}
      onChange={(_ev, _newValue, newPlainTextValue) => setDraft(newPlainTextValue)}
    >
      <Mention
        trigger="@"
        data={(query) => searchMentionOptions(options, query)}
        displayTransform={(_id, display) => display}
        renderSuggestion={(suggestion) => <div>{`${suggestion.name ?? ''} ${suggestion.label ?? ''}`}</div>}
      />
    </MentionsInput>
  );
}

/** Compose Email cell — plain input, or an `@`-mention input for recipient fields. */
export function EmailFieldCell(params: Params & EmailFieldParams) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);

  if (params.mention) {
    const options = filterMentionOptions(ctx.userMentions, [
      params.valueField === 'emailTo' ? '' : row.emailTo,
      params.valueField === 'emailCc' ? '' : row.emailCc,
      params.valueField === 'emailBcc' ? '' : row.emailBcc,
    ]);
    return (
      <CellFlex>
        <MentionEmailInput
          value={row[params.valueField]}
          options={options}
          placeholder={params.placeholder}
          onCommit={(next) => ctx.updateRow(row.id, { [params.valueField]: next } as Partial<MultiSubmitRow>)}
        />
      </CellFlex>
    );
  }

  return (
    <CellFlex>
      <CellInput
        type="text"
        placeholder={params.placeholder}
        value={row[params.valueField]}
        onChange={(e) => ctx.updateRow(row.id, { [params.valueField]: e.target.value } as Partial<MultiSubmitRow>)}
      />
    </CellFlex>
  );
}

/**
 * Which grading lines are visible: all when the product row is expanded, else the first.
 * Driven by row data only (not grid context) — the grading columns are already hidden
 * unless the Request Sample group is open, so no extra guard is needed here.
 */
function visibleLines(row: MultiSubmitRow): GradingGroupLine[] {
  return row.expanded ? row.gradingLines : row.gradingLines.slice(0, 1);
}

/** A grading-group column that stacks one control per grading line. */
export function GradingStackCell(params: Params & GradingStackParams) {
  const row = params.data;
  if (!row) return null;
  const ctx = ctxOf(params);
  const lines = visibleLines(row);
  const rowOpts = ctx.optionsFor(row.id);
  const baseOptions: SelectOption[] =
    params.options ??
    (params.optionsKey ? (rowOpts[params.optionsKey] as unknown as SelectOption[]) : undefined) ??
    (params.ctxOptionsKey ? (ctx as unknown as Record<string, SelectOption[]>)[params.ctxOptionsKey] : undefined) ??
    [];
  return (
    <Stack>
      {lines.map((line) => {
        const sizeOptionsForLine = params.field === 'size' ? gradingSizeOptions(rowOpts.gradingGroups, line.gradingGroup) : [];
        return (
          <StackItem key={line.id}>
            {params.field === 'size' ? (
              <FlipMultiSelect
                options={sizeOptionsForLine}
                value={(line.size ?? []).map((s) => ({ value: s, label: s }))}
                onChange={(selected: SelectOption[]) => ctx.updateGradingLine(row.id, line.id, { size: selected.map((s) => s.value) })}
                labelledBy={params.placeholder}
                overrideStrings={{ selectSomeItems: params.placeholder, allItemsAreSelected: 'All', selectAll: 'Select all' }}
                className="common-new-multiselect mssg-ms-ph w-100"
                disableSearch
                hasSelectAll
              />
            ) : params.kind === 'select' ? (
              <CellReactSelect
                value={String(line[params.field] ?? '')}
                options={baseOptions}
                placeholder={params.placeholder}
                onChange={(value) => {
                  const patch: Partial<GradingGroupLine> = { [params.field]: value } as Partial<GradingGroupLine>;
                  if (params.field === 'gradingGroup') patch.size = [];
                  ctx.updateGradingLine(row.id, line.id, patch);
                }}
              />
            ) : (
              <CellInput
                type="text"
                placeholder={params.placeholder}
                value={String(line[params.field] ?? '')}
                onChange={(e) => ctx.updateGradingLine(row.id, line.id, { [params.field]: e.target.value } as Partial<GradingGroupLine>)}
              />
            )}
          </StackItem>
        );
      })}
    </Stack>
  );
}

/** Plain text (or numeric) input cell — Costing Information Reference / Target Cost. */
export function TextInputCell(props: Params & TextInputParams) {
  const row = props.data;
  if (!row) return null;
  const ctx = ctxOf(props);
  /* Numeric cells mirror product-costing-detail/add: truncate to 2 decimals and drop any keystroke
     that is not a plain number, so sign characters never reach the row. */
  const commit = (raw: string) => {
    if (!props.numeric) {
      ctx.updateRow(row.id, { [props.valueField]: raw } as Partial<MultiSubmitRow>);
      return;
    }
    const restricted = restrictToTwoDecimals(raw);
    if (checkIsNumber(restricted || 0)) {
      ctx.updateRow(row.id, { [props.valueField]: restricted } as Partial<MultiSubmitRow>);
    }
  };
  return (
    <CellFlex>
      <CellInput
        type="text"
        placeholder={props.placeholder}
        value={row[props.valueField]}
        onChange={(e) => commit(e.target.value)}
        disabled={!!props.costingGated && !row.ciSupplier}
      />
    </CellFlex>
  );
}

/** Read-only display cell — Costing Information Supplier (top-selected) / Currency (company). No dropdown. */
export function DisplayCell(props: Params & DisplayCellParams) {
  const row = props.data;
  if (!row) return null;
  const ctx = ctxOf(props);
  const text =
    props.variant === 'supplier'
      ? ctx.supplierOptions.find((option) => option.value === ctx.selectedSupplierId)?.label ?? ''
      : ctx.supplierCurrencyById[row.ciSupplier] ?? '';
  return (
    <CellFlex>
      <DisplayField title={text || props.placeholder}>{text || props.placeholder}</DisplayField>
    </CellFlex>
  );
}

/** Which Core Product lines are visible: all when the row is expanded, else the first. */
function visibleCoreLines(row: MultiSubmitRow): CoreProductLine[] {
  return row.expanded ? row.coreLines : row.coreLines.slice(0, 1);
}

/**
 * A Core Product column that stacks one control per core line. `variant` selects the control:
 * `select` (Colourway/Size/Length), `quantity` (`+ │ input`, the `+` adds a line) or
 * `currency` (`£ │ input`, price fields).
 */
export function CoreStackCell(props: Params & CoreStackParams) {
  const row = props.data;
  if (!row) return null;
  const ctx = ctxOf(props);
  const lines = visibleCoreLines(row);
  const rowOpts = ctx.optionsFor(row.id);
  const ciCurrency = ctx.supplierCurrencyById[row.ciSupplier] ?? '';
  /* No Costing Information supplier -> no currency -> show no symbol at all (not a fallback). */
  const currencySymbol = ciCurrency ? checkCurrency(ciCurrency, undefined, true) || ciCurrency : '';
  /* Core Product describes the costing, so it is inert until the row opts into one. */
  const costingDisabled = !row.ciSupplier;
  const coreErrors = ctx.errorsFor(row.id);
  const hasError = props.variant !== 'select' && !props.computed && coreErrors.includes(`core.${props.field}` as CostingErrorField);
  const selectOptions: SelectOption[] =
    (props.optionsKey
      ? (rowOpts[props.optionsKey] as unknown as SelectOption[])
      : props.ctxOptionsKey
      ? (ctx as unknown as Record<string, SelectOption[]>)[props.ctxOptionsKey]
      : []) ?? [];
  /* Quantity and Price Per Unit take the same numeric rule as product-costing-detail/add: truncate to
     2 decimals and drop any keystroke that is not a plain number. */
  const commitCoreLine = (line: CoreProductLine, raw: string) => {
    const restricted = restrictToTwoDecimals(raw);
    if (!checkIsNumber(restricted || 0)) return;
    ctx.updateCoreLine(row.id, line.id, { [props.field]: restricted } as Partial<CoreProductLine>);
  };
  /* Total Price is derived rather than stored, so it can never drift from its inputs. */
  const totalFor = (line: CoreProductLine) => roundingOff(Number(line.quantity || 0) * Number(line.pricePerUnit || 0), 2);
  return (
    <Stack>
      {lines.map((line) => (
        <StackItem key={line.id}>
          {props.variant === 'select' ? (
            <FlipMultiSelect
              options={selectOptions}
              value={((line[props.field] as string[]) ?? []).map((v) => selectOptions.find((o) => o.value === v) ?? { value: v, label: v })}
              onChange={(selected: SelectOption[]) => {
                const values = selected.map((s) => s.value);
                /* Length depends on Size: clearing Size on a line also clears (and disables) its Length,
                   so a stale, no-longer-editable Length can never be submitted. */
                const patch = props.field === 'size' && values.length === 0 ? { size: [], length: [] } : { [props.field]: values };
                ctx.updateCoreLine(row.id, line.id, patch as Partial<CoreProductLine>);
              }}
              labelledBy={props.placeholder}
              overrideStrings={{ selectSomeItems: props.placeholder, allItemsAreSelected: 'All', selectAll: 'Select all' }}
              className="common-new-multiselect mssg-ms-ph w-100"
              disableSearch
              hasSelectAll
              disabled={costingDisabled || (props.field === 'length' && line.size.length === 0)}
            />
          ) : props.variant === 'quantity' ? (
            <CellInput
              type="text"
              placeholder={props.placeholder}
              value={String(line[props.field] ?? '')}
              onChange={(e) => commitCoreLine(line, e.target.value)}
              disabled={costingDisabled}
              $error={hasError}
            />
          ) : (
            <ComboBox $error={hasError}>
              {currencySymbol ? (
                <>
                  <CurrencyPrefix>{currencySymbol}</CurrencyPrefix>
                  <ComboDivider />
                </>
              ) : null}
              {props.computed ? (
                <BareInput type="text" placeholder={props.placeholder} value={totalFor(line)} disabled readOnly />
              ) : (
                <BareInput
                  type="text"
                  placeholder={props.placeholder}
                  value={String(line[props.field] ?? '')}
                  onChange={(e) => commitCoreLine(line, e.target.value)}
                  disabled={costingDisabled}
                />
              )}
            </ComboBox>
          )}
          {props.showInvite &&
            (() => {
              /* AG Grid leaves `data` stale for this value-less cell, so read the live flag from context. */
              const invited = ctx.isRowInvited(row.id);
              return (
                <CustomTooltip placement="top" tooltipClass="" tooltipText={props.inviteLabel ?? ''}>
                  <InviteIcon
                    aria-label={props.inviteLabel}
                    title={props.inviteLabel}
                    aria-pressed={invited}
                    $active={invited}
                    disabled={costingDisabled}
                    onClick={() => {
                      if (costingDisabled) return;
                      ctx.updateRow(row.id, { invite: !invited });
                    }}
                  >
                    <span className="icon-Invite f-20" />
                  </InviteIcon>
                </CustomTooltip>
              );
            })()}
        </StackItem>
      ))}
    </Stack>
  );
}

/** UoM select + inline +/delete, all inside one box per grading line: [ UoM ⌄ │ + 🗑 ]. */
export function UomActionsCell(props: Params & { options?: SelectOption[]; ctxOptionsKey?: 'unitOptions' }) {
  const row = props.data;
  if (!row) return null;
  const ctx = ctxOf(props);
  const lines = visibleLines(row);
  const uomOptions: SelectOption[] =
    props.options ?? (props.ctxOptionsKey ? (ctx as unknown as Record<string, SelectOption[]>)[props.ctxOptionsKey] : undefined) ?? [];
  return (
    <Stack>
      {lines.map((line) => (
        <StackItem key={line.id}>
          <ComboBox>
            <CellReactSelect
              bare
              value={line.uom}
              options={uomOptions}
              placeholder={ctx.t('RS.UOM')}
              onChange={(value) => ctx.updateGradingLine(row.id, line.id, { uom: value })}
            />
            <ComboDivider />
            <UomActions>
              <button
                type="button"
                className="bombiix-icon-container bombiix-icon-container--blue"
                onClick={() => ctx.addGradingLine(row.id)}
                aria-label={ctx.t('RS.ADD_GRADING_GROUP')}
                title={ctx.t('RS.ADD_GRADING_GROUP')}
              >
                <i className="bombiix-icon-add" />
              </button>
              <button
                type="button"
                className="bombiix-icon-container bombiix-icon-container--red"
                onClick={() => ctx.removeGradingLine(row.id, line.id)}
                disabled={row.gradingLines.length <= 1}
                aria-label={ctx.t('RS.REMOVE_GRADING_GROUP')}
                title={ctx.t('RS.REMOVE_GRADING_GROUP')}
              >
                <i className="bombiix-icon-delete" />
              </button>
            </UomActions>
          </ComboBox>
        </StackItem>
      ))}
    </Stack>
  );
}
